import { IconButton } from '@material-ui/core';
import TabletIcon from '@material-ui/icons/Tablet';
import { Entity } from '@sketchpixy/rubix/lib/L20n';
import _ from 'lodash';
import moment from 'moment';
import React from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { GREEN } from '../../_config/consts';
import CopyIconCustom from '../CustomIcons/CopyIconCustom.jsx';
import VisibilityInvisibleIconCustom from '../CustomIcons/VisibilityInvisibleIconCustom.jsx';
import VisibilityVisibleIconCustom from '../CustomIcons/VisibilityVisibleIconCustom.jsx';
import FormFlatContainer from '../forms/Elements/FormFlatContainer.jsx';
import MDAccordion from '../MDAccordion/MDAccordion.jsx';
import OnlineStatusBadge from './OnlineStatusBadge.jsx';
import {
  displayValue,
  formatNetworkStringValue,
  hasDisplayValue,
  isIdlePlayingContent,
  parseExtraInfo,
} from './hypertouchDeviceUtils';

const fieldLabelStyle = { fontSize: 18, margin: 0, marginBottom: 10, marginTop: 20, color: '#3f3f3f', fontWeight: 'bold' };
const fieldValueStyle = { fontSize: 18, margin: 0, color: '#3f3f3f' };

const accordionProps = {
  innerContainerStyle: { backgroundColor: '#fdfdfd' },
  contentContainerStyle: { paddingTop: 0 },
  titleStyle: { fontSize: 18 },
  elevation: 0,
};

function DetailField({ label, children }) {
  return (
    <div>
      <h5 style={fieldLabelStyle}>
        {_.isString(label) ? <Entity entity={label} /> : label}
      </h5>
      <div style={fieldValueStyle}>{children}</div>
    </div>
  );
}

function formatTimestamp(value) {
  if (!value) return '—';
  const date = moment(value);
  return date.isValid() ? date.format('LLL') : '—';
}

function formatBooleanValue(value) {
  if (_.isNil(value)) return '—';
  return <Entity entity={value ? 'yes' : 'no'} />;
}

function NetworkFields({ network, networkInterface }) {
  const ip = network && network.ip;
  const macAddress = network && network.macAddress;
  const interfaceName = formatNetworkStringValue((network && network.interfaceName) || networkInterface);
  const networkType = network && network.networkType;

  if (!ip && !macAddress && !interfaceName && !networkType) {
    return '—';
  }

  return (
    <div>
      <DetailField label="ipAddress">{ip || '—'}</DetailField>
      <DetailField label="macAddress">{macAddress || '—'}</DetailField>
      <DetailField label="networkInterface">{interfaceName || '—'}</DetailField>
      <DetailField label="networkType">{networkType || '—'}</DetailField>
    </div>
  );
}

function OptionalDetailField({ label, value, children }) {
  if (!hasDisplayValue(value) && !children) return null;
  return (
    <DetailField label={label}>
      {children || displayValue(value)}
    </DetailField>
  );
}

function UrlDetailField({ url }) {
  if (!hasDisplayValue(url)) return null;
  return (
    <DetailField label="contentUrl">
      <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: '#1976D2', wordBreak: 'break-all' }}>
        {url}
      </a>
    </DetailField>
  );
}

function CurrentPlayingContentFields({ content }) {
  if (isIdlePlayingContent(content)) {
    return (
      <DetailField label="contentPlayingStatus">
        <Entity entity="noContentPlaying" />
      </DetailField>
    );
  }

  const {
    applicationName,
    contentType,
    contentSource,
    contentStatus,
    contentPlayingStatus,
    contentId,
    apkPackageName,
    apkVersion,
    statusTime,
    scheduledStatus,
    scheduledId,
    messageCode,
    extraInfo,
  } = content;

  const { url, message, scheduled } = parseExtraInfo(extraInfo);
  const scheduledAppName = scheduled && scheduled.appName;
  const scheduledExtraContentId = scheduled && scheduled.contentId;

  return (
    <div>
      <OptionalDetailField label="contentType" value={contentType} />
      <OptionalDetailField label="applicationName" value={applicationName} />
      <OptionalDetailField label="contentSource" value={contentSource} />
      <OptionalDetailField label="contentStatus" value={contentStatus} />
      {!_.isNil(contentPlayingStatus) ? (
        <DetailField label="contentPlayingStatus">{formatBooleanValue(contentPlayingStatus)}</DetailField>
      ) : null}
      <OptionalDetailField label="contentId" value={contentId} />
      <OptionalDetailField label="apkPackageName" value={apkPackageName} />
      <OptionalDetailField label="apkVersion" value={apkVersion} />
      <UrlDetailField url={url} />
      <OptionalDetailField label="contentMessage" value={message} />
      {!_.isNil(statusTime) && statusTime !== '' ? (
        <DetailField label="contentStatusTime">{formatTimestamp(statusTime)}</DetailField>
      ) : null}
      {!_.isNil(scheduledStatus) ? (
        <DetailField label="scheduledStatus">{formatBooleanValue(scheduledStatus)}</DetailField>
      ) : null}
      <OptionalDetailField label="scheduledId" value={scheduledId} />
      {!_.isNil(messageCode) && messageCode !== '' ? (
        <DetailField label="messageCode">{messageCode}</DetailField>
      ) : null}
      {(hasDisplayValue(scheduledAppName) || hasDisplayValue(scheduledExtraContentId)) ? (
        <>
          <OptionalDetailField label="scheduledAppName" value={scheduledAppName} />
          <OptionalDetailField label="scheduledContentId" value={scheduledExtraContentId} />
        </>
      ) : null}
    </div>
  );
}

class PasswordField extends React.Component {
  constructor(props) {
    super(props);
    this.state = { visible: false };
  }

  render() {
    const { label, value } = this.props;
    const { visible } = this.state;

    if (!value) {
      return (
        <DetailField label={label}>—</DetailField>
      );
    }

    return (
      <DetailField label={label}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span>{visible ? value : '••••••••'}</span>
          <IconButton onClick={() => this.setState({ visible: !visible })} style={{ marginLeft: 4 }}>
            {visible ? (
              <VisibilityVisibleIconCustom style={{ width: 20, color: GREEN }} />
            ) : (
              <VisibilityInvisibleIconCustom style={{ width: 20, color: 'grey' }} />
            )}
          </IconButton>
          <CopyToClipboard text={value}>
            <IconButton>
              <CopyIconCustom style={{ width: 20, color: '#3f3f3f' }} />
            </IconButton>
          </CopyToClipboard>
        </div>
      </DetailField>
    );
  }
}

export default class HypertouchDeviceDetails extends React.Component {
  render() {
    const { device } = this.props;
    if (!device || !device.serial) return null;

    const { deviceName, serial, online, osVersion, lastUpdated, password, adminPassword, currentPlayingContent, lastReboot, network, networkInterface, categoryNames } = device;

    return (
      <div style={{ paddingTop: 0 }}>
        <FormFlatContainer headerStyle={{ marginBottom: 0 }}>
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
            <div className="rounded-view" style={{ marginRight: 15 }}>
              <TabletIcon style={{ fontSize: 48, color: '#3f3f3f' }} />
            </div>
            <div>
              <h4 style={{ fontSize: 30, margin: 0, color: '#3f3f3f', fontWeight: 'bold' }}>
                {deviceName || '—'}
              </h4>
              <OnlineStatusBadge online={online} style={{ marginTop: 8 }} />
            </div>
          </div>

          <DetailField label="serialNumber">{serial || '—'}</DetailField>
          <DetailField label="osVersion">{osVersion || '—'}</DetailField>
          <DetailField label="lastUpdated">{formatTimestamp(lastUpdated)}</DetailField>
          <DetailField label="lastReboot">{formatTimestamp(lastReboot)}</DetailField>
          <DetailField label="categoryNames">
            {!_.isEmpty(categoryNames) ? categoryNames.join(', ') : '—'}
          </DetailField>
          <PasswordField label="password" value={password} />
          <PasswordField label="adminPassword" value={adminPassword} />
          <MDAccordion
            {...accordionProps}
            title={<Entity entity="currentPlayingContent" />}
            containerstyle={{ marginTop: 20 }}
          >
            <CurrentPlayingContentFields content={currentPlayingContent} />
          </MDAccordion>
          <MDAccordion
            {...accordionProps}
            title={<Entity entity="network" />}
            containerstyle={{ marginTop: 15 }}
          >
            <NetworkFields network={network} networkInterface={networkInterface} />
          </MDAccordion>
        </FormFlatContainer>
      </div>
    );
  }
}
